import { WorkOrderCard } from '@/components/features/WorkOrders/components/WorkOrderCard';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent } from '@/components/ui/shadcn/card';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTranslations } from '@/hooks/useTranslations';
import { formatDate, isToday } from '@/lib/utils';
import { User as UserType, WorkOrder } from '@/types';
import { usePage } from '@inertiajs/react';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface JobsListProps {
    workOrders: WorkOrder[];
    selectedDate?: Date;
    onDateChange?: (date: Date) => void;
    auth: {
        user: UserType;
    };
    statuses?: Array<{ value: string; label: string; color: string; isTerminal?: boolean }>;
    priorities?: Array<{ value: string; label: string; color: string; icon?: string }>;
    types?: Array<{ value: string; label: string; color: string; icon?: string }>;
}

type FilterType = 'today' | 'overdue' | 'unscheduled' | 'completed' | 'all';

export default function JobsList({ workOrders, selectedDate: propSelectedDate, onDateChange, auth, statuses, priorities, types }: JobsListProps) {
    const { t } = useTranslations();
    const isMobile = useIsMobile();
    const page = usePage<{ translations?: Record<string, unknown> }>();
    const translations = page.props.translations ?? {};
    const [internalSelectedDate, setInternalSelectedDate] = useState(new Date());
    const selectedDate = propSelectedDate || internalSelectedDate;

    // Read URL parameters to set initial filter
    const getInitialFilter = (): FilterType => {
        if (typeof window === 'undefined') return 'today';
        const params = new URLSearchParams(window.location.search);
        if (params.get('unscheduled') === 'true') {
            return 'unscheduled';
        }
        // If there's a status filter (completed, in_progress), show all filtered results
        if (params.get('status')) {
            return 'all';
        }
        return 'today';
    };

    const [activeFilter, setActiveFilter] = useState<FilterType>(getInitialFilter());
    const previousSelectedDate = useRef<Date>(selectedDate);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [isScrolling, setIsScrolling] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [scrollDirection, setScrollDirection] = useState<'left' | 'right' | null>(null);
    const [scrollProgress, setScrollProgress] = useState(0);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);

    // Infinite scroll state
    const [visibleItemsCount, setVisibleItemsCount] = useState(10);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

    // Get day names from translations
    const jobsListTranslations = (translations.work_orders as Record<string, unknown>)?.['jobs_list'] as Record<string, unknown> | undefined;
    // `day_names` is a list, but tolerate a legacy comma-joined string: indexing a string
    // positionally yields single characters instead of day labels.
    const rawDayNames = jobsListTranslations?.['day_names'];
    const dayNames = Array.isArray(rawDayNames)
        ? (rawDayNames as string[])
        : typeof rawDayNames === 'string' && rawDayNames.trim() !== ''
          ? rawDayNames.split(',').map((day) => day.trim())
          : ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    // Helper function to check if current user is assigned to work order.
    // Mirrors the backend scope (assigned_user_id OR an assignments row), so a work order
    // the server considers ours is never hidden here.
    const isUserAssigned = (workOrder: WorkOrder): boolean => {
        // Check new assignments system (lead or helper)
        const hasAssignment = (workOrder.assignments ?? []).some((assignment) => {
            // Check both user_id field and user.id (user_id might not be in the mapped resource)
            return assignment.user_id === auth.user.id || assignment.user?.id === auth.user.id;
        });

        // Legacy assigned_user_id still counts even when assignments rows exist
        return hasAssignment || workOrder.assigned_user_id === auth.user.id;
    };

    // Get assigned work orders
    const assignedWorkOrders = workOrders.filter((workOrder) => isUserAssigned(workOrder));

    // Helper function to check if a work order status is terminal (completed or canceled)
    const isTerminalStatus = (status: string | undefined): boolean => {
        return status === 'completed' || status === 'canceled' || status === 'cancelled' || status === 'closed';
    };

    // Calculate counts for each filter
    const filterCounts = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let allCount = 0;
        let todayCount = 0;
        let overdueCount = 0;
        let unscheduledCount = 0;
        let completedCount = 0;

        assignedWorkOrders.forEach((workOrder) => {
            // Exclude terminal statuses from all other filters
            const isTerminal = isTerminalStatus(workOrder.status);

            if (isTerminal) {
                completedCount++;
                return; // Skip terminal statuses for other counts
            }

            allCount++;

            const workOrderDate = workOrder.estimated_date ? new Date(workOrder.estimated_date) : null;

            if (workOrderDate) {
                workOrderDate.setHours(0, 0, 0, 0);

                if (workOrderDate.getTime() === today.getTime()) {
                    todayCount++;
                }

                if (workOrderDate < today) {
                    overdueCount++;
                }
            } else {
                unscheduledCount++;
            }
        });

        return {
            all: allCount,
            today: todayCount + overdueCount, // Include overdue tasks in today's count
            overdue: overdueCount,
            unscheduled: unscheduledCount,
            completed: completedCount,
        };
    }, [assignedWorkOrders]);

    // Track previous date to detect calendar interactions
    useEffect(() => {
        previousSelectedDate.current = selectedDate;
    }, [selectedDate]);

    // Filter work orders based on active filter and assignment
    const filteredWorkOrders = workOrders
        .filter((workOrder) => {
            // First filter: only show work orders assigned to current user
            if (!isUserAssigned(workOrder)) {
                return false;
            }

            const isTerminal = isTerminalStatus(workOrder.status);

            // Apply filter based on activeFilter
            switch (activeFilter) {
                case 'completed':
                    // Show only terminal statuses (completed or canceled)
                    return isTerminal;

                case 'today': {
                    // Exclude terminal statuses
                    if (isTerminal) return false;

                    // Show items for the selected date from calendar AND overdue items (only when selected date is today)
                    const todayWorkOrderDate = workOrder.estimated_date ? new Date(workOrder.estimated_date) : null;
                    if (!todayWorkOrderDate) return false;

                    todayWorkOrderDate.setHours(0, 0, 0, 0);

                    // Check if task is overdue (estimated date is in the past)
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const todayIsOverdue = todayWorkOrderDate < today;

                    // Show overdue items only when the selected date is today
                    if (todayIsOverdue && isToday(selectedDate)) {
                        return true;
                    }

                    // Show items estimated for the selected date
                    const todaySelectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
                    return todayWorkOrderDate.getTime() === todaySelectedDateOnly.getTime();
                }

                case 'overdue': {
                    // Exclude terminal statuses
                    if (isTerminal) return false;

                    const overdueWorkOrderDate = workOrder.estimated_date ? new Date(workOrder.estimated_date) : null;
                    if (!overdueWorkOrderDate) return false;

                    overdueWorkOrderDate.setHours(0, 0, 0, 0);
                    const overdueToday = new Date();
                    overdueToday.setHours(0, 0, 0, 0);
                    return overdueWorkOrderDate < overdueToday;
                }

                case 'unscheduled':
                    // Exclude terminal statuses
                    if (isTerminal) return false;

                    return !workOrder.estimated_date;

                case 'all':
                default:
                    // Exclude terminal statuses from "all" filter
                    return !isTerminal;
            }
        })
        .sort((a, b) => {
            // Sort overdue tasks to the top
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const aIsOverdue = a.estimated_date && new Date(a.estimated_date) < today;
            const bIsOverdue = b.estimated_date && new Date(b.estimated_date) < today;

            // If one is overdue and the other isn't, overdue comes first
            if (aIsOverdue && !bIsOverdue) return -1;
            if (!aIsOverdue && bIsOverdue) return 1;

            // If both are overdue or both are not overdue, sort by estimated date
            if (a.estimated_date && b.estimated_date) {
                return new Date(a.estimated_date).getTime() - new Date(b.estimated_date).getTime();
            }

            // If one has no estimated date, put it after the one with a date
            if (!a.estimated_date && b.estimated_date) return 1;
            if (a.estimated_date && !b.estimated_date) return -1;

            // If neither has estimated date, maintain original order
            return 0;
        });

    // Reset visible items when filter or date changes
    useEffect(() => {
        setVisibleItemsCount(10);
    }, [activeFilter, selectedDate]);

    // Get visible work orders for infinite scroll
    const visibleWorkOrders = useMemo(() => {
        return filteredWorkOrders.slice(0, visibleItemsCount);
    }, [filteredWorkOrders, visibleItemsCount]);

    // Check if there are more items to load
    const hasMoreItems = filteredWorkOrders.length > visibleItemsCount;

    // Intersection Observer for infinite scroll
    useEffect(() => {
        if (!loadMoreTriggerRef.current || !hasMoreItems) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoadingMore) {
                    setIsLoadingMore(true);
                    // Simulate loading delay
                    setTimeout(() => {
                        setVisibleItemsCount((prev) => Math.min(prev + 10, filteredWorkOrders.length));
                        setIsLoadingMore(false);
                    }, 500);
                }
            },
            { threshold: 0.1 },
        );

        observer.observe(loadMoreTriggerRef.current);

        return () => {
            observer.disconnect();
        };
    }, [hasMoreItems, isLoadingMore, filteredWorkOrders.length]);

    // Get job count for a specific date.
    // Must agree with the 'today' filter above, or the badge promises rows the tab won't show.
    const getJobCountForDate = (date: Date) => {
        return workOrders.filter((workOrder) => {
            // First filter: only count work orders assigned to current user
            if (!isUserAssigned(workOrder)) {
                return false;
            }

            // Exclude terminal statuses from date-based counts
            if (isTerminalStatus(workOrder.status)) {
                return false;
            }

            // Unscheduled jobs have no day to sit on — they live under the "Unscheduled" tab
            if (!workOrder.estimated_date) {
                return false;
            }

            const workOrderDate = new Date(workOrder.estimated_date);
            if (Number.isNaN(workOrderDate.getTime())) {
                return false;
            }
            workOrderDate.setHours(0, 0, 0, 0);

            const today = new Date();
            today.setHours(0, 0, 0, 0);

            // Overdue jobs are surfaced on today's view, matching the 'today' filter
            if (workOrderDate < today && isToday(date)) {
                return true;
            }

            const selectedDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

            return workOrderDate.getTime() === selectedDateOnly.getTime();
        }).length;
    };

    // Generate week dates for date picker
    const getWeekDates = (date: Date) => {
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay());

        return Array.from({ length: 7 }, (_, i) => {
            const day = new Date(startOfWeek);
            day.setDate(startOfWeek.getDate() + i);
            return day;
        });
    };

    // Generate adjacent week dates for preview
    const getAdjacentWeekDates = (date: Date, direction: 'left' | 'right') => {
        const weekOffset = direction === 'left' ? -7 : 7;
        const adjacentDate = new Date(date);
        adjacentDate.setDate(date.getDate() + weekOffset);
        return getWeekDates(adjacentDate);
    };

    const weekDates = getWeekDates(selectedDate);

    // Handle touch start for swipe detection
    const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
        const touch = event.touches[0];
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        setScrollDirection(null);
        setScrollProgress(0);
    };

    // Handle touch move for scroll progress tracking
    const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
        if (isScrolling || isTransitioning) return;

        const touch = event.touches[0];
        const deltaX = touch.clientX - touchStartX.current;
        const deltaY = touch.clientY - touchStartY.current;

        // Only track horizontal swipes
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
            const direction = deltaX > 0 ? 'right' : 'left';
            const progress = Math.min(Math.abs(deltaX) / 100, 1); // Normalize to 0-1

            setScrollDirection(direction);
            setScrollProgress(progress);
        }
    };

    // Handle touch end for swipe detection
    const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
        if (isScrolling) return;

        const touch = event.changedTouches[0];
        const touchEndX = touch.clientX;
        const touchEndY = touch.clientY;

        const deltaX = touchEndX - touchStartX.current;
        const deltaY = touchEndY - touchStartY.current;

        // Only trigger if horizontal swipe is more significant than vertical
        if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
            setIsScrolling(true);
            setIsTransitioning(true);

            // Start transition with preview
            setTimeout(() => {
                if (deltaX > 0) {
                    // Swiped right - previous week
                    const newDate = new Date(selectedDate);
                    newDate.setDate(selectedDate.getDate() - 7);
                    if (onDateChange) {
                        onDateChange(newDate);
                    } else {
                        setInternalSelectedDate(newDate);
                    }
                } else {
                    // Swiped left - next week
                    const newDate = new Date(selectedDate);
                    newDate.setDate(selectedDate.getDate() + 7);
                    if (onDateChange) {
                        onDateChange(newDate);
                    } else {
                        setInternalSelectedDate(newDate);
                    }
                }

                // Reset scroll position
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollLeft = 0;
                }

                // Reset states after transition
                setTimeout(() => {
                    setIsScrolling(false);
                    setIsTransitioning(false);
                    setScrollDirection(null);
                    setScrollProgress(0);
                }, 300);
            }, 100);
        } else {
            // Reset if swipe wasn't significant enough
            setScrollDirection(null);
            setScrollProgress(0);
        }
    };

    // Reset scroll position when selectedDate changes
    useEffect(() => {
        if (scrollContainerRef.current && !isScrolling) {
            scrollContainerRef.current.scrollLeft = 0;
        }
    }, [selectedDate, isScrolling]);

    // Navigate to previous week
    const navigateToPreviousWeek = useCallback(() => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() - 7);
        if (onDateChange) {
            onDateChange(newDate);
        } else {
            setInternalSelectedDate(newDate);
        }
    }, [selectedDate, onDateChange]);

    // Navigate to next week
    const navigateToNextWeek = useCallback(() => {
        const newDate = new Date(selectedDate);
        newDate.setDate(selectedDate.getDate() + 7);
        if (onDateChange) {
            onDateChange(newDate);
        } else {
            setInternalSelectedDate(newDate);
        }
    }, [selectedDate, onDateChange]);

    // Arrow key navigation (desktop only)
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Only handle on desktop (not mobile)
            if (isMobile) return;

            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                navigateToPreviousWeek();
            } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                navigateToNextWeek();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [navigateToPreviousWeek, navigateToNextWeek]);

    return (
        <div className="space-y-4">
            {/* Date Picker - Centered */}
            <div className="flex flex-col items-center space-y-3">
                {/* Month/Year Header */}
                <div className="text-center">
                    <h3 className="font-semibold">{formatDate(selectedDate)}</h3>
                </div>

                {/* Calendar with Navigation Arrows (Desktop) */}
                <div className="mx-auto flex w-full max-w-[500px] items-center gap-2 overflow-hidden">
                    {/* Previous Week Button - Desktop Only */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={navigateToPreviousWeek}
                        className="hidden h-10 w-10 flex-shrink-0 p-0 md:flex"
                        aria-label="Previous week"
                    >
                        <ChevronLeft className="h-5 w-5" />
                    </Button>

                    {/* Scrollable Week View */}
                    <div
                        ref={scrollContainerRef}
                        className={`min-w-0 flex-1 overflow-x-auto transition-all duration-500 ease-in-out md:max-w-[500px] [&::-webkit-scrollbar]:hidden ${
                            isTransitioning ? 'scale-98 opacity-90' : 'scale-100 opacity-100'
                        }`}
                        style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            transform: scrollDirection
                                ? `translateX(${scrollDirection === 'left' ? -scrollProgress * 20 : scrollProgress * 20}px)`
                                : 'translateX(0)',
                        }}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div className="mx-auto flex min-w-max gap-2 px-2 sm:px-4">
                            {/* Previous week preview */}
                            {scrollDirection === 'right' && scrollProgress > 0 && (
                                <>
                                    {getAdjacentWeekDates(selectedDate, 'left').map((date, index) => {
                                        const jobCount = getJobCountForDate(date);
                                        return (
                                            <div key={`prev-${index}`} className="flex min-w-[44px] flex-col items-center opacity-50">
                                                <div className="text-muted-foreground mb-1 text-xs font-medium">{dayNames[index]}</div>
                                                <div className="relative">
                                                    <Button variant="ghost" size="sm" className="h-10 w-10 p-0">
                                                        {date.getDate()}
                                                    </Button>
                                                    {jobCount > 0 && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center p-0 text-xs"
                                                        >
                                                            {jobCount}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}

                            {/* Current week */}
                            {weekDates.map((date, index) => {
                                const jobCount = getJobCountForDate(date);
                                return (
                                    <div key={index} className="flex min-w-[44px] flex-col items-center">
                                        {/* Day Label */}
                                        <div className="text-muted-foreground mb-1 text-xs font-medium">{dayNames[index]}</div>

                                        {/* Date Button */}
                                        <div className="relative">
                                            <Button
                                                variant={isToday(date) ? 'default' : 'ghost'}
                                                size="sm"
                                                className={`h-10 w-10 p-0 ${
                                                    selectedDate.toDateString() === date.toDateString() ? 'bg-accent text-accent-foreground' : ''
                                                }`}
                                                onClick={() => {
                                                    // If "all" filter is active when clicking a date, switch to "today" to respect date selection
                                                    if (activeFilter === 'all') {
                                                        setActiveFilter('today');
                                                    }

                                                    if (onDateChange) {
                                                        onDateChange(date);
                                                    } else {
                                                        setInternalSelectedDate(date);
                                                    }
                                                }}
                                            >
                                                {date.getDate()}
                                            </Button>
                                            {jobCount > 0 && (
                                                <Badge
                                                    variant="secondary"
                                                    className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center p-0 text-xs"
                                                >
                                                    {jobCount}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Next week preview */}
                            {scrollDirection === 'left' && scrollProgress > 0 && (
                                <>
                                    {getAdjacentWeekDates(selectedDate, 'right').map((date, index) => {
                                        const jobCount = getJobCountForDate(date);
                                        return (
                                            <div key={`next-${index}`} className="flex min-w-[44px] flex-col items-center opacity-50">
                                                <div className="text-muted-foreground mb-1 text-xs font-medium">{dayNames[index]}</div>
                                                <div className="relative">
                                                    <Button variant="ghost" size="sm" className="h-10 w-10 p-0">
                                                        {date.getDate()}
                                                    </Button>
                                                    {jobCount > 0 && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center p-0 text-xs"
                                                        >
                                                            {jobCount}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    </div>

                    {/* Next Week Button - Desktop Only */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={navigateToNextWeek}
                        className="hidden h-10 w-10 flex-shrink-0 p-0 md:flex"
                        aria-label="Next week"
                    >
                        <ChevronRight className="h-5 w-5" />
                    </Button>
                </div>
            </div>

            {/* Filter Buttons */}
            <div className="grid w-full grid-cols-2 gap-2 md:grid-cols-5">
                <Button
                    variant={activeFilter === 'today' ? 'default' : 'outline'}
                    size="default"
                    onClick={() => {
                        setActiveFilter('today');
                        // Switch calendar to today's date
                        const today = new Date();
                        if (onDateChange) {
                            onDateChange(today);
                        } else {
                            setInternalSelectedDate(today);
                        }
                    }}
                    className="col-span-2 flex h-12 items-center justify-between gap-2 px-3 text-sm md:col-span-1 md:h-10 md:text-xs"
                >
                    <span>{t('work_orders.jobs_list.filters.today', 'Today')}</span>
                    <Badge variant={activeFilter === 'today' ? 'secondary' : 'outline'} className="px-1.5 py-0 text-xs">
                        {filterCounts.today}
                    </Badge>
                </Button>
                <Button
                    variant={activeFilter === 'overdue' ? 'default' : 'outline'}
                    size="default"
                    onClick={() => setActiveFilter('overdue')}
                    className="flex h-12 items-center justify-between gap-2 px-3 text-sm md:h-10 md:text-xs"
                >
                    <span>{t('work_orders.jobs_list.filters.overdue', 'Overdue')}</span>
                    <Badge variant={activeFilter === 'overdue' ? 'secondary' : 'outline'} className="px-1.5 py-0 text-xs">
                        {filterCounts.overdue}
                    </Badge>
                </Button>
                <Button
                    variant={activeFilter === 'unscheduled' ? 'default' : 'outline'}
                    size="default"
                    onClick={() => setActiveFilter('unscheduled')}
                    className="flex h-12 items-center justify-between gap-2 px-3 text-sm md:h-10 md:text-xs"
                >
                    <span>{t('work_orders.jobs_list.filters.unscheduled', 'Unscheduled')}</span>
                    <Badge variant={activeFilter === 'unscheduled' ? 'secondary' : 'outline'} className="px-1.5 py-0 text-xs">
                        {filterCounts.unscheduled}
                    </Badge>
                </Button>
                <Button
                    variant={activeFilter === 'completed' ? 'default' : 'outline'}
                    size="default"
                    onClick={() => setActiveFilter('completed')}
                    className="flex h-12 items-center justify-between gap-2 px-3 text-sm md:h-10 md:text-xs"
                >
                    <span>{t('work_orders.jobs_list.filters.completed', 'Completed')}</span>
                    <Badge variant={activeFilter === 'completed' ? 'secondary' : 'outline'} className="px-1.5 py-0 text-xs">
                        {filterCounts.completed}
                    </Badge>
                </Button>
                <Button
                    variant={activeFilter === 'all' ? 'default' : 'outline'}
                    size="default"
                    onClick={() => setActiveFilter('all')}
                    className="flex h-12 items-center justify-between gap-2 px-3 text-sm md:h-10 md:text-xs"
                >
                    <span>{t('work_orders.jobs_list.filters.all', 'All')}</span>
                    <Badge variant={activeFilter === 'all' ? 'secondary' : 'outline'} className="px-1.5 py-0 text-xs">
                        {filterCounts.all}
                    </Badge>
                </Button>
            </div>

            <div
                className={`transition-all duration-500 ease-in-out ${
                    isTransitioning ? 'translate-y-2 transform opacity-70' : 'translate-y-0 transform opacity-100'
                }`}
            >
                {filteredWorkOrders.length > 0 ? (
                    <div className="space-y-3">
                        {visibleWorkOrders.map((workOrder) => (
                            <WorkOrderCard key={workOrder.id} workOrder={workOrder} statuses={statuses} priorities={priorities} types={types} />
                        ))}
                        {/* Loading trigger and indicator */}
                        {hasMoreItems && (
                            <div ref={loadMoreTriggerRef} className="min-h-[60px] py-4">
                                {isLoadingMore && (
                                    <div className="flex items-center justify-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="border-primary h-6 w-6 animate-spin rounded-full border-2 border-t-transparent" />
                                            <span className="text-muted-foreground text-xs">
                                                {t('work_orders.jobs_list.loading_more', 'Loading more...')}
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ) : (
                    <Card>
                        <CardContent className="p-8 text-center">
                            <CalendarIcon className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
                            <h3 className="mb-2 text-lg font-semibold">
                                {activeFilter === 'today' && `No tasks scheduled for ${formatDate(selectedDate)}`}
                                {activeFilter === 'overdue' && t('work_orders.jobs_list.no_overdue_tasks', 'No overdue tasks')}
                                {activeFilter === 'unscheduled' && t('work_orders.jobs_list.no_unscheduled_tasks', 'No unscheduled tasks')}
                                {activeFilter === 'completed' && t('work_orders.jobs_list.no_completed_tasks', 'No completed tasks')}
                                {activeFilter === 'all' && `No tasks assigned - ${formatDate(selectedDate)}`}
                            </h3>
                            <p className="text-muted-foreground">
                                {activeFilter === 'today' && ''}
                                {activeFilter === 'overdue' &&
                                    t('work_orders.jobs_list.no_tasks_for_filter', 'No work orders match the selected filter')}
                                {activeFilter === 'unscheduled' &&
                                    t('work_orders.jobs_list.no_tasks_for_filter', 'No work orders match the selected filter')}
                                {activeFilter === 'completed' &&
                                    t('work_orders.jobs_list.no_tasks_for_filter', 'No work orders match the selected filter')}
                                {activeFilter === 'all' && ''}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}
